// ============================================================
// Game — Loop, turn management, result detection
// ============================================================

import { type Game, type GameConfig, type GameResult, type Move, type BoardState, DEFAULT_CONFIG } from './types';
import { createInitialState } from './board';
import { generateLegalMoves, applyMove, isInCheck } from './moves';

// --------------- Game Factory ---------------

export function createGame(config: Partial<GameConfig> = {}): Game {
  const fullConfig = { ...DEFAULT_CONFIG, ...config };
  const initialState = createInitialState();

  return {
    states: [initialState],
    moves: [],
    result: null,
    config: fullConfig,
  };
}

// --------------- Game Logic ---------------

/** Get the current board state */
export function currentState(game: Game): BoardState {
  return game.states[game.states.length - 1];
}

/** Make a move, returning a new Game */
export function makeGameMove(game: Game, move: Move): Game {
  if (game.result) throw new Error('Game is already over');

  const state = currentState(game);
  const nextState = applyMove(state, move);
  const nextMoves = generateLegalMoves(nextState);

  // Check for game end
  let result: GameResult | null = null;
  if (nextMoves.length === 0) {
    if (isInCheck(nextState, nextState.activePlayer)) {
      result = { kind: 'checkmate', winner: state.activePlayer };
    } else {
      result = { kind: 'stalemate' };
    }
  } else if (nextState.halfMoveClock >= 100) {
    result = { kind: 'draw', reason: 'fifty-move' };
  } else {
    // Threefold repetition
    const hash = nextState.hash;
    let count = 0;
    for (const s of game.states) {
      if (s.hash === hash) count++;
    }
    if (count >= 2) { // nextState makes 3rd occurrence
      result = { kind: 'draw', reason: 'repetition' };
    }
  }

  // Mark checkmate on the move
  const finalMove: Move = result?.kind === 'checkmate'
    ? { ...move, isCheckmate: true, isCheck: true }
    : move;

  return {
    ...game,
    states: [...game.states, nextState],
    moves: [...game.moves, finalMove],
    result,
  };
}

/** Undo the last move, returning a new Game (or null if no moves) */
export function undoMove(game: Game): Game | null {
  if (game.states.length <= 1) return null;
  return {
    ...game,
    states: game.states.slice(0, -1),
    moves: game.moves.slice(0, -1),
    result: null,
  };
}

/** Get legal moves for the current position */
export function getLegalMoves(game: Game): Move[] {
  return generateLegalMoves(currentState(game));
}

/** Get legal moves for a specific position */
export function getMovesFrom(game: Game, x: number, y: number, w: number): Move[] {
  return getLegalMoves(game).filter(m => m.from.x === x && m.from.y === y && m.from.w === w);
}
